import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GET_FESTIVAL_SETTINGS } from "./routes/settings/graphql";
import { GET_EDITIONS } from "./routes/editions/graphql";
import { EXPORT_FESTIVAL } from "./routes/editionsBands/graphql";
import { toast } from "react-toastify";

// App.tsx
const API = process.env.REACT_APP_API

const getCurrentUserToken = () => {
  const token = localStorage.getItem("token");

  if (token) return JSON.parse(token);

  return undefined;
};

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    if(graphQLErrors[0].message.includes('Not Authorised!') || graphQLErrors[0].message.includes('jwt malformed')) {
      // Not Authorised! jwt malformed
      localStorage.removeItem("token");
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    console.log("gql error", JSON.stringify(graphQLErrors));
  }

  forward(operation);
});

const authorizationLink = setContext(async (_req, { headers }) => {
  if (_req.operationName === "signin") {
    return {
      headers,
    };
  }

  const authorization = getCurrentUserToken();
  const authHeader = authorization ? { authorization: authorization } : {};

  return {
    headers: {
      ...headers,
      ...authHeader,
    } as Headers,
  };
});

// const refreshLink = new TokenRefreshLink({
//   accessTokenField: 'newToken',
//   fetchAccessToken: async () => {
//     const token =  undefined//getCurrentUserToken()

//     if (!token) {
//       return null
//     }

//     // ts-ignore
//     const response = await fetch(`${API}`, {
//       body: JSON.stringify({
//         query: `
//           mutation {
//             refresh {
//               token
//             }
//           }
//         `,
//       }),
//       credentials: 'include',
//       headers: {
//         'content-type': 'application/json',
//       },
//       method: 'POST',
//     })

//     return response.json()
//   },
//   handleError: (error) => {
//     console.error('Cannot refresh access token:', error)
//   },
//   handleFetch: (newToken) => {
//     //setCurrentUserToken(newToken)
//   },
// //   handleResponse: () => (response: {
// //     data: { refresh: Mutation['refresh'] }
// //     errors?: unknown[]
// //   }) => {
// //     if (!response) {
// //       try {
// //         Sentry.captureMessage(
// //           'refreshLink handleResponse: response was undefined or null',
// //         )
// //       } catch (ex) {
// //         console.log('refreshLink handleResponse: error calling captureMessage')
// //       }
// //       clearCurrentUserToken()
// //       window.location.reload()

// //       return
// //     }

// //     if (response.errors) {
// //       logError(JSON.stringify(response.errors))
// //       clearCurrentUserToken()
// //       window.location.reload()

// //       return
// //     }

// //     return { newToken: response.data?.refresh?.token }
// //   },
//   isTokenValidOrUndefined: () => {
//       return true
// //     const token = getCurrentUserToken()

// //     if (!token) {
// //       return true
// //     }
// //     // @ts-expect-error
// //     if (token && decode(token)?.exp * 1000 > Date.now()) {
// //       return true
// //     }
// //     return false
//   },
// })

// const httpLink = createUploadLink({
//   credentials: "include",
//   fetch,
//   uri: API,
// });

const AUTO_EXPORT_MUTATIONS = new Set([
  "createUpdateBand",
  "createBand",
  "updateBand",
  "createBooking",
  "updateBooking",
  "updateBookings",
  "deleteBandBooking",
  "createUpdateAnnouncement",
  "updateEdition",
]);

let clientRef: ApolloClient<any> | null = null;
let exportInFlight = false;

export const triggerLiveEditionExport = async (options?: { suppressToast?: boolean }) => {
  if (exportInFlight || !clientRef) return;

  exportInFlight = true;
  try {
    const settings = await clientRef.query({
      query: GET_FESTIVAL_SETTINGS,
      fetchPolicy: "network-only",
    });
    const editionId = settings.data?.settings?.[0]?.editionId;
    if (!editionId) return;

    const editions = await clientRef.query({
      query: GET_EDITIONS,
      fetchPolicy: "cache-first",
    });
    const edition = editions.data?.festivalEditions?.find(
      (item: any) => item.id === editionId
    );
    const name = edition?.name ?? "Live Edition";

    await clientRef.query({
      query: EXPORT_FESTIVAL,
      variables: { id: editionId, name },
      fetchPolicy: "network-only",
    });
    if (!options?.suppressToast) {
      toast("Updated ascociated announcements");
    }
  } catch (err) {
    toast("Error updating app");
    console.error("auto-export failed", err);
  } finally {
    exportInFlight = false;
  }
};

const exportTriggerLink = new ApolloLink((operation, forward) => {
  const observable = forward(operation);
  return observable.map((response) => {
    const { skipAutoExportMessage, options: operationOptions } =
      operation.getContext();
    if (
      operation.operationName &&
      AUTO_EXPORT_MUTATIONS.has(operation.operationName)
    ) {
      const suppressToastFromOptions =
        typeof operationOptions?.suppressToast === "boolean"
          ? operationOptions.suppressToast
          : undefined;

      triggerLiveEditionExport({
        suppressToast:
          suppressToastFromOptions ?? Boolean(skipAutoExportMessage),
      });
    }
    return response;
  });
});

const link = from([
  authorizationLink,
  errorLink,
  exportTriggerLink,
  new HttpLink({ uri: API }),
]);

const cache = new InMemoryCache();

export const apolloClient = new ApolloClient({
  uri: API,
  cache,
  link,
  defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" } },
});

clientRef = apolloClient;
